import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

/**
 * Hook de Tipos de Leads
 * - Lista principal: usa tree=1 (retorna roots + até 3 níveis de children)
 * - Subtipos por id_pai: usa o parâmetro correto `parent_id`
 * - Mutations: POST/PUT/DELETE padrão
 */
export const useTypes = (page = 1, limit = 100, search = "", includeInactive = false) => {
  const queryClient = useQueryClient();

  // === Query principal: ÁRVORE ===
  const query = useQuery(
    ["types", page, limit, search, includeInactive],
    () => {
      const params = new URLSearchParams({
        tree: "1",
        page: String(page),
        limit: String(limit),
        search,
      });
      if (includeInactive) params.set("include_inactive", "1");
      return fetchData(`/types?${params.toString()}`);
    },
    {
      // Catálogo muda pouco; cache maior evita refetchs duplicados no boot
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      select: (response) => ({
        types: (response.data || []).map(t => ({
          ...t,
          allowed_positions: Array.isArray(t.allowed_positions) ? t.allowed_positions : [],
          source_urls: Array.isArray(t.source_urls) ? t.source_urls : [],
          documents: Array.isArray(t.documents) ? t.documents : [],
        })),
        pagination: response.pagination || { total: 0 },
      }),
    }
  );

  // === Hook auxiliar: lista flat por id_pai ===
  const useFetchSubtypes = (idPai, subIncludeInactive = includeInactive) => {
    return useQuery(
      ["subtypes", idPai, subIncludeInactive],
      () => {
        const params = new URLSearchParams({ parent_id: String(idPai) });
        if (subIncludeInactive) params.set("include_inactive", "1");
        return fetchData(`/types?${params.toString()}`).then((res) => res.data || []);
      },
      {
        enabled: !!idPai,
        staleTime: 5000,
        refetchOnWindowFocus: false,
      }
    );
  };

  // === Mutations padrão ===
  const updateMutation = useMutation(
    (type) => {
      const method = type.id ? "PUT" : "POST";
      const endpoint = type.id ? `/types/${type.id}` : `/types`;
      return fetchData(endpoint, method, type);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("types");
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => fetchData(`/types/${id}`, "DELETE"),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("types");
      },
    }
  );

  return { ...query, useFetchSubtypes, updateMutation, deleteMutation };
};
